/**
 * open-in-new-tab.tsx — Link / nút sang MÀN KHÁC của module Nhân viên AI: luôn mở TAB MỚI (màn đang làm giữ nguyên),
 * kèm biểu tượng "mở ở tab mới" nhỏ + chữ ẩn cho trình đọc màn hình. Quyết định link nào mở tab mới: open-target-lib.ts.
 *
 *   <OpenInNewTab to="/integrations">Kết nối thêm kênh</OpenInNewTab>                 — dạng link chữ
 *   <OpenInNewTab to="/ai/knowledge?tao=1" variant="outline" size="sm">Tạo kho</OpenInNewTab> — dạng nút
 *   <AiFlowLink to={path} ctx={{ screen: 'hub' }}>…</AiFlowLink>  — tự chọn: cùng tab (luồng đang làm) hay tab mới
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button, type ButtonProps } from '@/components/ui/button'
import { i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { opensInNewTab, type LinkContext } from './open-target-lib'

/** Chữ gợi ý "Mở ở tab mới" (đọc i18n lúc dùng). */
export const newTabHint = (): string => i18n.t('aiComp:newTab.hint')

/** Mở một đường dẫn của ứng dụng ở tab mới (dùng cho onClick không phải thẻ <a>). */
export function openInNewTab(path: string): void {
  window.open(path, '_blank', 'noopener,noreferrer')
}

export function OpenInNewTab({ to, children, variant, size, className, title, showIcon = true, 'aria-label': ariaLabel }: {
  to: string
  children: ReactNode
  /** Có `variant` → hiện dạng nút; không có → link chữ màu chính. */
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  className?: string
  title?: string
  showIcon?: boolean
  'aria-label'?: string
}) {
  const { t } = useTranslation('aiComp')
  const hint = t('newTab.hint')
  const content = (
    <>
      {children}
      {showIcon && <ExternalLink className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />}
      <span className="sr-only"> ({hint.toLowerCase()})</span>
    </>
  )
  const anchor = { href: to, target: '_blank', rel: 'noopener noreferrer', title: title ?? hint, 'aria-label': ariaLabel }
  if (variant) {
    return (
      <Button variant={variant} size={size} className={className} asChild>
        <a {...anchor}>{content}</a>
      </Button>
    )
  }
  return <a {...anchor} className={cn('inline-flex items-center gap-1 text-primary hover:underline', className)}>{content}</a>
}

/** Link tự chọn theo nguyên tắc: điều hướng nội bộ của luồng → cùng tab (<Link>); màn khác → tab mới. */
export function AiFlowLink({ to, ctx, children, variant, size, className }: {
  to: string
  ctx: LinkContext
  children: ReactNode
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  className?: string
}) {
  if (opensInNewTab(to, ctx)) return <OpenInNewTab to={to} variant={variant} size={size} className={className}>{children}</OpenInNewTab>
  if (variant) {
    return <Button variant={variant} size={size} className={className} asChild><Link to={to}>{children}</Link></Button>
  }
  return <Link to={to} className={cn('text-primary hover:underline', className)}>{children}</Link>
}
